// ./src/contexts/TagsContext/TagsContext.tsx
//
// Thin context wrapper around the useTags hook.
//
// All derived data (activeTags, totalTagsCount, hasTags, getTagsByIds,
// createTagInline, getNextAutoColor) now lives in the hook itself.
//
// Usage:
//   <TagsProvider autoLoad>
//     <MyComponent />          {/* reads via useTagsContext() */}
//   </TagsProvider>
//
// Two accessors are exported:
//   useTagsContext()         — throws if no provider (for components that *require* it)
//   useOptionalTagsContext() — returns null (for components that can work with or without)

import React, { createContext, useContext } from "react";

import {
  useTags,
  UseTagsOptions,
  UseTagsReturn,
} from "@/hooks/useTags";

// ============================================
// Context
// ============================================

const TagsContext = createContext<UseTagsReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface TagsProviderProps extends UseTagsOptions {
  children: React.ReactNode;
}

/**
 * Wraps children with a shared Tags state.
 * Each provider instance creates its own independent hook,
 * so two providers on different pages won't interfere.
 */
export const TagsProvider: React.FC<TagsProviderProps> = ({
  children,
  ...options
}) => {
  const value = useTags(options);

  return (
    <TagsContext.Provider value={value}>
      {children}
    </TagsContext.Provider>
  );
};

// ============================================
// Strict accessor — throws when no provider
// ============================================

/**
 * Returns the Tags context value.
 * Throws if called outside an <TagsProvider>.
 */
export const useTagsContext = (): UseTagsReturn => {
  const context = useContext(TagsContext);

  if (!context) {
    throw new Error(
      "useTagsContext must be used within an <TagsProvider>. " +
      "Wrap your component tree with <TagsProvider> to fix this."
    );
  }

  return context;
};

// ============================================
// Safe accessor — returns null when no provider
// ============================================

/**
 * Returns the Tags context value, or null if no provider is present.
 * Use this in components that should work both inside and outside a provider
 * (e.g. reusable list/drawer components).
 */
export const useOptionalTagsContext = (): UseTagsReturn | null => {
  return useContext(TagsContext);
};